
import {
  Vue, Component, Prop,
} from 'vue-property-decorator';
import moment from 'moment';
import { BoxStatusType, Connection } from '@/types';
import EditConnectionStatusDialog from '@/components/connections/EditConnectionStatusDialog.vue';

moment.locale('it');

@Component({
  components: {
    EditConnectionStatusDialog,
  },
})
export default class ConnectionStatus extends Vue {
  @Prop(Object) readonly connection!: Connection;

  loading = false
  editDialog = false

  get isDisabled() {
    return this.loading;
  }

  get vTooltipColor(): string {
    return this.connection ? this.tooltipColor(this.connection?.verbaleAllaccio as BoxStatusType) : '';
  }

  get vChipColor(): string {
    return this.connection ? this.chipColor(this.connection?.verbaleAllaccio as BoxStatusType) : '';
  }

  get vTooltipText(): string {
    return this.connection ? this.tooltipText('Verbale di allaccio', this.connection?.verbaleAllaccio as BoxStatusType) : '';
  }

  get vChipOutlined(): boolean {
    return this.connection
      ? this.chipOutlined(this.connection?.verbaleAllaccio as BoxStatusType)
      : false;
  }

  tooltipColor(value: BoxStatusType | null): string {
    switch (value) {
      case null:
      case 'todo':
        return 'grey';

      case 'done':
        return 'success';

      case 'waiting':
        return 'orange';

      case 'na':
        return 'grey';

      default:
        break;
    }

    return '';
  }

  tooltipText(prefix: string, value: BoxStatusType | null): string {
    switch (value) {
      case null:
      case 'todo':
        return `${prefix} (assente)`;

      case 'na':
        return `${prefix} (non applicabile)`;

      case 'waiting':
        return `${prefix} (in attesa di terzi)`;

      default:
        break;
    }
    return prefix;
  }

  chipColor(value: BoxStatusType | null): string {
    switch (value) {
      case null:
      case 'todo':
        return 'grey lighten-2';

      case 'done':
        return 'statusDone';

      case 'waiting':
        return 'orange';

      case 'na':
        return 'grey';

      default:
        break;
    }

    return '';
  }

  chipOutlined(value: BoxStatusType | null): boolean {
    switch (value) {
      case 'na':
        return true;

      default:
        break;
    }

    return false;
  }

  get suggestedEgridConnectionDate1(): string | null {
    return this.connection
      && this.connection.suggestedEgridConnectionDate1
      && moment(this.connection.suggestedEgridConnectionDate1).isValid()
      ? moment(this.connection.suggestedEgridConnectionDate1).format('L')
      : null;
  }
  get suggestedEgridConnectionDate2(): string | null {
    return this.connection
      && this.connection.suggestedEgridConnectionDate2
      && moment(this.connection.suggestedEgridConnectionDate2).isValid()
      ? moment(this.connection.suggestedEgridConnectionDate2).format('L')
      : null;
  }

  get suggestedEgridConnectionTime1(): string | null {
    return this.connection?.suggestedEgridConnectionTime1 || null;
  }

  get suggestedEgridConnectionTime2(): string | null {
    return this.connection?.suggestedEgridConnectionTime2 || null;
  }

  get egridConnectionDate(): string | null {
    return this.connection.egridConnectionDate
      && moment(this.connection.egridConnectionDate).isValid()
      ? moment(this.connection.egridConnectionDate).format('L')
      : null;
  }

  get egridConnectionActiveDate(): string | null {
    return this.connection.egridConnectionActiveDate
      && moment(this.connection.egridConnectionActiveDate).isValid()
      ? moment(this.connection.egridConnectionActiveDate).format('L')
      : null;
  }

  get egridConnectionTime(): string | null {
    return this.connection?.egridConnectionTime || null;
  }

  get egridConnectionNotes(): string | null {
    return this.connection?.egridConnectionNotes || null;
  }

  get egridConnectionStatus(): string | null {
    return this.connection?.egridConnectionStatus ?? null;
  }

  // Allacciamento (seconda colonna)

  get connectionColumnClass(): string {
    // Se il task è in lavorazione e l'allaccio è confermato, la colonna è verde.
    if (this.egridConnectionStatus === 'connected') return 'green';

    // Se la data è concordata...
    if (this.egridConnectionStatus === 'not-confirmed'
    || this.egridConnectionStatus === 'no-confirmation-required') return 'yellow-white';

    // Se la data è impostata e confermata...
    if (this.egridConnectionStatus === 'confirmed'
      && this.connection.egridConnectionDate !== null) {
      // ... Mostra la colonna gialla se la data è precedente alla data odierna,
      // viceversa arancione.
      const todayDate = moment().startOf('day').toDate();
      const connectionDate = moment(this.connection.egridConnectionDate).startOf('day').toDate();

      return moment(todayDate).isSameOrBefore(connectionDate) ? 'yellow' : 'orange';
    }

    // Se l'allaccio è non applicabile
    if (this.egridConnectionStatus === 'na') return 'grey lighten-3';

    // Se l'allaccio è da riprogrammare
    if (this.egridConnectionStatus === 'ko') return 'red';

    return '';
  }

  get connectionTooltipColor(): string {
    // Allacciamento
    switch (this.egridConnectionStatus) {
      case 'suggested':
      case 'not-confirmed':
      case 'no-confirmation-required':
        return 'orange';

      case 'confirmed':
      case 'connected':
        return 'success';

      case 'na':
        return 'grey';

      case 'ko':
      default:
        return 'error';
    }
  }

  get connectionTooltipText() {
    // Allacciamento
    switch (this.egridConnectionStatus) {
      case 'suggested':
        return 'Date proposte';

      case 'not-confirmed':
        return 'Data concordata (da confermare a portale)';

      case 'no-confirmation-required':
        return 'Data concordata (non necessaria conferma a portale)';

      case 'confirmed':
        return 'Data confermata';

      case 'connected':
        return 'Impianto in esercizio';

      case 'ko':
        return 'Allaccio KO da riprogrammare';

      case 'na':
        return 'Allaccio non applicabile';

      default:
        return 'Data prevista per l\'allacciamento da definire';
    }
  }

  get connectionClass(): string {
    return !this.egridConnectionStatus
      ? 'grey--text text--lighten-2'
      : '';
  }

  get connectionText(): string {
    switch (this.egridConnectionStatus) {
      case 'suggested': {
        const suggestedDateTime1 = this.suggestedEgridConnectionTime1
          ? `${this.suggestedEgridConnectionDate1} ${this.suggestedEgridConnectionTime1}`
          : `${this.suggestedEgridConnectionDate1}`;

        let suggestedDateTime2 = '';
        if (this.suggestedEgridConnectionDate2) {
          suggestedDateTime2 = this.suggestedEgridConnectionTime2
            ? `<br>${this.suggestedEgridConnectionDate2} ${this.suggestedEgridConnectionTime2}`
            : `<br>${this.suggestedEgridConnectionDate2}`;
        }

        return `ALLACCIAMENTO<br>${suggestedDateTime1}${suggestedDateTime2}`;
      }

      case 'not-confirmed':
      case 'no-confirmation-required':
      case 'confirmed':
        return this.egridConnectionTime
          ? `ALLACCIAMENTO<br>${this.egridConnectionDate} ${this.egridConnectionTime}`
          : `ALLACCIAMENTO<br>${this.egridConnectionDate}`;

      case 'connected':
        return this.egridConnectionActiveDate
          ? `ALLACCIAMENTO<br>${this.egridConnectionActiveDate}`
          : 'ALLACCIAMENTO';

      case 'na':
        return 'Non applicabile';

      case 'ko':
        return 'ALLACCIAMENTO<br>KO';

      default:
        return 'da definire';
    }
  }

  // Azioni

  async onEdit() {
    // Non permette l'apertura del dialog durante il caricamento
    if (this.loading || this.isDisabled) return;

    console.log(`${this.constructor.name}: onEdit()`); // eslint-disable-line no-console

    // La connessione deve esistere per procedere
    if (this.connection) {
      // Seleziona la Connection prima di aprire il dialog
      this.loading = true;
      await this.$store.dispatch('selectConnection', { id: this.connection.id });
      this.editDialog = true;
      this.loading = false;
    }
  }
}
